import {
    IFlujoDefinicion,
    INodo,
    NODO_TIPO,
    NodoTipo,
} from 'src/app/models/notificacion/whatsapp_flujo.model';

/**
 * Las puntas sueltas del flujo, en lenguaje de usuario, para guiar mientras se
 * edita en vez de rechazar al guardar.
 *
 * NO reimplementa la validación: el backend es la autoridad
 * (`back/.../whatsapp-flujo/validacion.ts`). Estos códigos son la contraparte de
 * ARISTA_SIN_DESTINO, SIN_ARISTA_HANDOFF y CAMINO_SIN_TERMINAL; si esas reglas
 * cambian, acá también.
 */

export const PENDIENTE = {
    BOTON_SIN_DESTINO: 'BOTON_SIN_DESTINO',
    MENSAJE_SIN_SIGUIENTE: 'MENSAJE_SIN_SIGUIENTE',
    PASO_SIN_TEXTO: 'PASO_SIN_TEXTO',
    PASO_SIN_FINAL: 'PASO_SIN_FINAL',
    SIN_SALIDA_A_PERSONA: 'SIN_SALIDA_A_PERSONA',
} as const;

export type CodigoPendiente = (typeof PENDIENTE)[keyof typeof PENDIENTE];

export interface IPendiente {
    codigo: CodigoPendiente;
    /** La pregunta o la tarea, como se la muestra al usuario. */
    titulo: string;
    /** Solo cuando el título no se explica solo. */
    detalle?: string;
    /** Índice en el `FormArray` de nodos, para poder actuar sin volver a buscar. */
    indiceNodo: number | null;
    /** Índice dentro del `FormArray` de opciones de ese nodo. */
    indiceOpcion: number | null;
}

const LARGO_MAX_CITA = 32;

const recortar = (texto: string): string => (
    texto.length > LARGO_MAX_CITA ? `${texto.slice(0, LARGO_MAX_CITA)}…` : texto
);

/** Nombra un paso por su texto. El id interno (`paso_2`) no va a pantalla. */
export const describirNodo = (nodo: INodo, esInicial: boolean): string => {
    if (esInicial) {
        return 'el primer paso';
    }

    const texto = String(nodo?.texto || '').trim();
    return texto ? `«${recortar(texto)}»` : 'un paso sin texto';
};

/** Aristas que realmente llevan a algún lado. Mismo criterio que `aristasValidas` del backend. */
const aristasValidas = (nodos: INodo[]): Array<{ desde: string; hasta: string }> => {
    const ids = new Set(nodos.map((nodo) => nodo.id));
    const aristas: Array<{ desde: string; hasta: string }> = [];

    for (const nodo of nodos) {
        if (nodo.tipo === NODO_TIPO.CHOICE) {
            for (const opcion of nodo.opciones || []) {
                if (opcion.destinoNodoId && ids.has(opcion.destinoNodoId)) {
                    aristas.push({ desde: nodo.id, hasta: opcion.destinoNodoId });
                }
            }
        }

        if (nodo.tipo === NODO_TIPO.MESSAGE && nodo.siguienteNodoId && ids.has(nodo.siguienteNodoId)) {
            aristas.push({ desde: nodo.id, hasta: nodo.siguienteNodoId });
        }
    }

    return aristas;
};

const alcanzablesDesde = (origen: string, nodos: INodo[]): Set<string> => {
    const porOrigen = new Map<string, string[]>();
    for (const arista of aristasValidas(nodos)) {
        const lista = porOrigen.get(arista.desde) ?? [];
        lista.push(arista.hasta);
        porOrigen.set(arista.desde, lista);
    }

    const visitados = new Set<string>([origen]);
    const cola = [origen];

    while (cola.length > 0) {
        const actual = cola.shift() as string;
        for (const siguiente of porOrigen.get(actual) ?? []) {
            if (!visitados.has(siguiente)) {
                visitados.add(siguiente);
                cola.push(siguiente);
            }
        }
    }

    return visitados;
};

const esTerminal = (nodo: INodo): boolean => nodo.tipo === NODO_TIPO.END || nodo.tipo === NODO_TIPO.HANDOFF;

/**
 * Nodos desde los que NINGÚN camino llega a un END o HANDOFF — mismo criterio
 * que `CAMINO_SIN_TERMINAL` en el backend (`verificarCaminoATerminal`): BFS
 * REVERSO desde el conjunto de terminales, caminando las aristas al revés
 * (destino -> origen). Un walk hacia adelante entraría en loop infinito sobre
 * un ciclo legítimo como "volver al menú" y daría falsos negativos; ver el
 * doc-comment de esa función en el backend.
 */
export const nodosSinFinal = (nodos: INodo[]): Set<string> => {
    const predecesoresDe = new Map<string, string[]>();
    for (const arista of aristasValidas(nodos)) {
        const lista = predecesoresDe.get(arista.hasta) ?? [];
        lista.push(arista.desde);
        predecesoresDe.set(arista.hasta, lista);
    }

    const terminales = nodos.filter(esTerminal).map((nodo) => nodo.id);
    const puedeLlegarATerminal = new Set<string>(terminales);
    const cola = [...terminales];

    while (cola.length > 0) {
        const actual = cola.shift() as string;
        for (const predecesor of predecesoresDe.get(actual) ?? []) {
            if (!puedeLlegarATerminal.has(predecesor)) {
                puedeLlegarATerminal.add(predecesor);
                cola.push(predecesor);
            }
        }
    }

    const sinFinal = new Set<string>();
    for (const nodo of nodos) {
        if (!puedeLlegarATerminal.has(nodo.id)) {
            sinFinal.add(nodo.id);
        }
    }

    return sinFinal;
};

/**
 * Pasos no alcanzables desde el inicio por ningún camino hacia adelante —
 * mismo criterio que `NODO_INALCANZABLE` en el backend
 * (`verificarAlcanzabilidadDesdeInicio`): BFS hacia adelante desde el nodo
 * inicial sobre `aristasValidas`, no sobre lo que el usuario escribió como
 * destino (un destino borrado no cuenta como arista).
 */
export const nodosInalcanzables = (nodos: INodo[], idNodoInicial: string): Set<string> => {
    const ids = new Set(nodos.map((nodo) => nodo.id));
    if (!ids.has(idNodoInicial)) {
        return new Set();
    }

    const alcanzables = alcanzablesDesde(idNodoInicial, nodos);
    const inalcanzables = new Set<string>();

    for (const nodo of nodos) {
        if (!alcanzables.has(nodo.id)) {
            inalcanzables.add(nodo.id);
        }
    }

    return inalcanzables;
};

/**
 * Todo lo que falta para cerrar el circuito: primero las puntas sueltas, que abren trabajo nuevo; después los textos.
 * Un paso con punta suelta no se reporta además como PASO_SIN_FINAL: sería el mismo problema dos veces.
 */
export const calcularPendientes = (
    definicion: IFlujoDefinicion | null,
    idNodoInicial: string,
): IPendiente[] => {
    const nodos = definicion?.nodos ?? [];
    if (!nodos.length) {
        return [];
    }

    const ids = new Set(nodos.map((nodo) => nodo.id));
    const puntas: IPendiente[] = [];
    const textos: IPendiente[] = [];
    const idsConPuntaSuelta = new Set<string>();

    nodos.forEach((nodo, indiceNodo) => {
        const esInicial = nodo.id === idNodoInicial;

        if (nodo.tipo === NODO_TIPO.CHOICE) {
            (nodo.opciones || []).forEach((opcion, indiceOpcion) => {
                if (opcion.destinoNodoId && ids.has(opcion.destinoNodoId)) {
                    return;
                }

                idsConPuntaSuelta.add(nodo.id);
                puntas.push({
                    codigo: PENDIENTE.BOTON_SIN_DESTINO,
                    titulo: `Cuando toquen «${opcion.etiqueta || opcion.id}», ¿qué pasa?`,
                    indiceNodo,
                    indiceOpcion,
                });
            });

            if (!(nodo.opciones || []).length) {
                idsConPuntaSuelta.add(nodo.id);
                puntas.push({
                    codigo: PENDIENTE.BOTON_SIN_DESTINO,
                    titulo: `Agregá los botones de ${describirNodo(nodo, esInicial)}`,
                    indiceNodo,
                    indiceOpcion: null,
                });
            }
        }

        if (nodo.tipo === NODO_TIPO.MESSAGE && !(nodo.siguienteNodoId && ids.has(nodo.siguienteNodoId))) {
            idsConPuntaSuelta.add(nodo.id);
            puntas.push({
                codigo: PENDIENTE.MENSAJE_SIN_SIGUIENTE,
                titulo: `Después de ${describirNodo(nodo, esInicial)}, ¿qué pasa?`,
                indiceNodo,
                indiceOpcion: null,
            });
        }

        if (!String(nodo.texto || '').trim()) {
            textos.push({
                codigo: PENDIENTE.PASO_SIN_TEXTO,
                titulo: esInicial
                    ? 'Falta qué contesta si no tocan ningún botón'
                    : `Falta el mensaje de ${describirNodo(nodo, false)}`,
                indiceNodo,
                indiceOpcion: null,
            });
        }
    });

    const idsSinFinal = nodosSinFinal(nodos);
    const finales: IPendiente[] = [];

    nodos.forEach((nodo, indiceNodo) => {
        if (idsConPuntaSuelta.has(nodo.id) || !idsSinFinal.has(nodo.id)) {
            return;
        }

        finales.push({
            codigo: PENDIENTE.PASO_SIN_FINAL,
            titulo: `Desde ${describirNodo(nodo, nodo.id === idNodoInicial)} no hay forma de terminar la conversación`,
            detalle: 'Al menos un botón o el paso siguiente tiene que llevar a cerrar la conversación o a una persona.',
            indiceNodo,
            indiceOpcion: null,
        });
    });

    const pendientes = [...puntas, ...textos, ...finales];

    if (ids.has(idNodoInicial)) {
        const alcanzables = alcanzablesDesde(idNodoInicial, nodos);
        const hayPersona = nodos.some((nodo) => nodo.tipo === NODO_TIPO.HANDOFF && alcanzables.has(nodo.id));

        if (!hayPersona) {
            pendientes.push({
                codigo: PENDIENTE.SIN_SALIDA_A_PERSONA,
                titulo: 'Falta una salida a una persona',
                detalle: 'Al menos un camino tiene que terminar con alguien del equipo.',
                indiceNodo: null,
                indiceOpcion: null,
            });
        }
    }

    return pendientes;
};

/** Tipos de paso que se ofrecen al resolver una punta, en orden de uso esperado. */
export const TIPOS_PARA_RESOLVER: NodoTipo[] = [
    NODO_TIPO.HANDOFF,
    NODO_TIPO.END,
    NODO_TIPO.CHOICE,
    NODO_TIPO.MESSAGE,
];
